import { motion } from "framer-motion";
import { seeded } from "../../lib/motion";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

/** Ambient drifting motes — background texture for hero and ceremony stages. */
export function Particles({
  count = 18,
  className = "",
  color = "#dd54eb",
}: {
  count?: number;
  className?: string;
  color?: string;
}) {
  const reduced = usePrefersReducedMotion();
  if (reduced) return null;

  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      {Array.from({ length: count }).map((_, i) => {
        const size = 2 + seeded(i, 1) * 4;
        const left = seeded(i, 2) * 100;
        const delay = seeded(i, 3) * 6;
        const duration = 9 + seeded(i, 4) * 10;
        const drift = (seeded(i, 5) - 0.5) * 60;
        return (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${left}%`,
              bottom: -10,
              width: size,
              height: size,
              background: color,
              boxShadow: `0 0 ${size * 3}px ${color}`,
            }}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 0.75, 0], y: [-20, -420], x: [0, drift] }}
            transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
          />
        );
      })}
    </div>
  );
}

/** Radial light rays behind a revealed card — scaled by rarity. */
export function RayBurst({
  active,
  className = "",
  intensity = 2,
}: {
  active: boolean;
  className?: string;
  intensity?: 0 | 1 | 2 | 3;
}) {
  const reduced = usePrefersReducedMotion();
  if (!active || intensity === 0) return null;

  const peak = intensity >= 3 ? 0.6 : intensity === 2 ? 0.42 : 0.28;

  return (
    <motion.img
      aria-hidden
      src="/art/burst.svg"
      alt=""
      className={`pointer-events-none absolute left-1/2 top-1/2 -z-10 w-[180%] max-w-none -translate-x-1/2 -translate-y-1/2 ${className}`}
      initial={{ opacity: 0, scale: 0.4, rotate: 0 }}
      animate={
        reduced
          ? { opacity: peak * 0.5, scale: 1 }
          : { opacity: [0, peak, peak * 0.6], scale: [0.4, 1.1, 1], rotate: 360 }
      }
      transition={
        reduced
          ? { duration: 0.2 }
          : { opacity: { duration: 1.2 }, scale: { duration: 1.2, ease: [0.16, 1, 0.3, 1] }, rotate: { duration: 26, repeat: Infinity, ease: "linear" } }
      }
    />
  );
}
